import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import {
  StonePriceRuleBasis,
  StonePriceRuleStoneType,
  type StaffProfileStoresItem,
  type StonePriceRule,
} from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { BASE_CURRENCY } from '@/shared/catalog/base-currency';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyStonePriceRuleForm,
  fromStonePriceRule,
  stoneRuleFieldPath,
  toStonePriceRuleCreate,
  toStonePriceRuleUpdate,
} from '../api/stone-price-rule-mappers';
import {
  useCreateStonePriceRule,
  useUpdateStonePriceRule,
} from '../hooks/use-stone-price-rule-mutations';
import {
  stonePriceRuleFormSchema,
  type StonePriceRuleFormInput,
  type StonePriceRuleFormValues,
} from '../schemas/stone-price-rule-form-schema';

const ALL_STORES = '__all__';

export interface StonePriceRuleDialogProps {
  /** The band being edited; null creates one. */
  rule: StonePriceRule | null;
  stores: ReadonlyArray<Pick<StaffProfileStoresItem, 'id' | 'name'>>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Body({
  rule,
  stores,
  onClose,
}: {
  rule: StonePriceRule | null;
  stores: StonePriceRuleDialogProps['stores'];
  onClose: () => void;
}) {
  const { t } = useTranslation(['dictionary', 'common', 'products']);
  const message = useErrorMessage();
  const create = useCreateStonePriceRule();
  const update = useUpdateStonePriceRule();
  const pending = create.isPending || update.isPending;
  const form = useForm<StonePriceRuleFormInput, unknown, StonePriceRuleFormValues>({
    resolver: useI18nResolver(stonePriceRuleFormSchema),
    defaultValues: rule ? fromStonePriceRule(rule) : emptyStonePriceRuleForm(),
  });
  const basis = form.watch('basis');
  const mapField = useCallback((field: string) => stoneRuleFieldPath(basis)(field), [basis]);
  const applyFieldErrors = useApplyFieldErrors(form.setError, mapField);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      if (rule) {
        await update.mutateAsync({ id: rule.id, body: toStonePriceRuleUpdate(values) });
      } else {
        await create.mutateAsync(toStonePriceRuleCreate(values));
      }
      onClose();
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  const money = (name: 'fromCost' | 'toCost', label: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <MoneyInput
              currency={BASE_CURRENCY}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              aria-invalid={fieldState.invalid}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
  const carat = (name: 'fromCarat' | 'toCarat', label: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input inputMode="decimal" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert" className="sm:col-span-2">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="stoneType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dictionary:stonePricing.fields.stoneType')}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange} disabled={rule !== null}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(StonePriceRuleStoneType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`products:configurator.stoneType.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {rule ? (
                <FormDescription>
                  {t('dictionary:stonePricing.fields.stoneTypeFixed')}
                </FormDescription>
              ) : null}
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="storeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dictionary:stonePricing.fields.store')}</FormLabel>
              <Select
                value={field.value || ALL_STORES}
                onValueChange={(v) => field.onChange(v === ALL_STORES ? '' : v)}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={ALL_STORES}>
                    {t('dictionary:stonePricing.allStores')}
                  </SelectItem>
                  {stores.map((store) => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="basis"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t('dictionary:stonePricing.fields.basis')}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(StonePriceRuleBasis).map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`dictionary:stonePricing.basis.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                {t(`dictionary:stonePricing.basisHint.${field.value}`)}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        {basis === 'carat' ? (
          <>
            {carat('fromCarat', t('dictionary:stonePricing.fields.fromCarat'))}
            {carat('toCarat', t('dictionary:stonePricing.fields.toCarat'))}
          </>
        ) : (
          <>
            {money('fromCost', t('dictionary:stonePricing.fields.fromCost'))}
            {money('toCost', t('dictionary:stonePricing.fields.toCost'))}
          </>
        )}
        <FormField
          control={form.control}
          name="marginPct"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t('dictionary:stonePricing.fields.margin')}</FormLabel>
              <div className="relative sm:max-w-48">
                <FormControl>
                  <Input inputMode="decimal" className="pr-7" {...field} />
                </FormControl>
                <span
                  className="text-muted-foreground pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-xs"
                  aria-hidden
                >
                  %
                </span>
              </div>
              <FormDescription>{t('dictionary:stonePricing.fields.marginHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter className="sm:col-span-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending
              ? t('common:actions.saving')
              : rule
                ? t('common:actions.save')
                : t('dictionary:stonePricing.create.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Create or edit one markup band; the stone type is fixed once saved. */
export function StonePriceRuleDialog({
  rule,
  stores,
  open,
  onOpenChange,
}: StonePriceRuleDialogProps) {
  const { t } = useTranslation('dictionary');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {rule ? t('stonePricing.edit.title') : t('stonePricing.create.title')}
          </DialogTitle>
          <DialogDescription>{t('stonePricing.create.description')}</DialogDescription>
        </DialogHeader>
        {open ? (
          <Body
            key={rule?.updatedAt ?? 'new'}
            rule={rule}
            stores={stores}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
